import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/common/ConfirmDialog';

interface Formulario {
  id: string;
  nome: string;
  descricao?: string;
  tipo_arquivo: string;
  url: string;
  nome_arquivo: string;
  tamanho: number;
  data_upload: string;
}

const FormulariosAdminPage: React.FC = () => {
  const [formularios, setFormularios] = useState<Formulario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);

  const carregar = async () => {
    try {
      const res = await api.get('/formularios');
      setFormularios(res.data.formularios || []);
    } catch {
      toast.error('Erro ao carregar formulários');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!arquivo || !nome.trim()) return;
    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      formData.append('nome', nome.trim());
      if (descricao.trim()) formData.append('descricao', descricao.trim());
      await api.post('/formularios', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Formulário enviado com sucesso!');
      setNome('');
      setDescricao('');
      setArquivo(null);
      carregar();
    } catch {
      toast.error('Erro ao enviar formulário');
    } finally {
      setEnviando(false);
    }
  };

  const [formularioParaRemover, setFormularioParaRemover] = useState<{ id: string; nome: string } | null>(null);

  const handleRemover = (id: string, nome: string) => {
    setFormularioParaRemover({ id, nome });
  };

  const confirmarRemocao = async () => {
    if (!formularioParaRemover) return;
    const { id } = formularioParaRemover;
    setFormularioParaRemover(null);
    try {
      await api.delete(`/formularios/${id}`);
      toast.success('Formulário removido');
      carregar();
    } catch {
      toast.error('Erro ao remover formulário');
    }
  };

  const formatarTamanho = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const iconeArquivo = (tipo: string) => {
    if (tipo === 'pdf') {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (['xlsx', 'xls'].includes(tipo)) {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m6 10V7M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
        </svg>
      );
    }
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Formulários para Download</h2>

      {/* Upload */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Adicionar novo formulário</h3>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome do formulário *</label>
              <input
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Ex: Formulário de Solicitação de Compras"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição (opcional)</label>
              <input
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                placeholder="Breve descrição do formulário"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo (DOCX, PDF, XLSX) *</label>
            <input
              type="file"
              accept=".docx,.doc,.pdf,.xlsx,.xls"
              onChange={e => setArquivo(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Máximo 20MB</p>
          </div>
          <button
            type="submit"
            disabled={enviando || !arquivo || !nome.trim()}
            className="px-6 py-2 bg-bravo-primary text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enviando ? 'Enviando...' : 'Adicionar Formulário'}
          </button>
        </form>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          Formulários cadastrados ({formularios.length})
        </h3>
        {carregando ? (
          <p className="text-gray-400 text-sm">Carregando...</p>
        ) : formularios.length === 0 ? (
          <p className="text-gray-400 text-sm italic">Nenhum formulário cadastrado ainda.</p>
        ) : (
          <div className="space-y-3">
            {formularios.map(f => (
              <div key={f.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-gray-500 flex-shrink-0">{iconeArquivo(f.tipo_arquivo)}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{f.nome}</p>
                    <p className="text-xs text-gray-500">
                      {f.nome_arquivo} • {formatarTamanho(f.tamanho)} • {f.tipo_arquivo.toUpperCase()}
                    </p>
                    {f.descricao && <p className="text-xs text-gray-400 mt-0.5">{f.descricao}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 text-sm text-bravo-primary border border-bravo-primary rounded-md hover:bg-red-50 transition-colors"
                  >
                    Baixar
                  </a>
                  <button
                    onClick={() => handleRemover(f.id, f.nome)}
                    className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!formularioParaRemover}
        title="Remover formulário"
        message={`Remover "${formularioParaRemover?.nome}"?`}
        confirmText="Remover"
        onConfirm={confirmarRemocao}
        onCancel={() => setFormularioParaRemover(null)}
      />
    </div>
  );
};

export default FormulariosAdminPage;
